import {
  SectionId,
  SectionType,
  FEE_TYPE_CATEGORIES,
  BUY_SELL_CATEGORIES,
  DEPOSIT_WITHDRAW_CATEGORIES,
  FX_SPOT_CATEGORIES,
  SETTLEMENT_ACCOUNT_CATEGORIES,
  DEDUCTION_REBATE_CATEGORIES,
} from '../../types';
import { MultiColumnRulesBinaryCategoriesSection } from '../ConfigurationSections/MultiColumnRulesBinaryCategoriesSection/MultiColumnRulesBinaryCategoriesSection';
import { InstrumentIdentifiers } from '../ConfigurationSections/InstrumentIdentifiers/InstrumentIdentifiers';
import { SingleColumnWithDateSection } from '../ConfigurationSections/SingleColumnWithDateSection/SingleColumnWithDateSection';
import { SingleColumnSection } from '../ConfigurationSections/SingleColumnSection/SingleColumnSection';
import { SingleColumnWithLabelSection } from '../ConfigurationSections/SingleColumnWithLabelSection/SingleColumnWithLabelSection';
import { MathOperationsSection } from '../ConfigurationSections/MathOperationsSection/MathOperationsSection';
import { CurrencySection } from '../ConfigurationSections/CurrencySection/CurrencySection';
import { MultiColumnRulesWithCategoriesSection } from '../ConfigurationSections/MultiColumnRulesWithCategoriesSection/MultiColumnRulesWithCategoriesSection';
import { SectionRendererProps } from './types';
import { useSectionRenderer } from './useSectionRenderer';

export const SectionRenderer = (props: SectionRendererProps) => {
  const {
    section,
    templateId,
    instrumentType,
    availableHeaders,
    expanded,
    onChange,
  } = props;

  useSectionRenderer(props);

  // Map section types to components
  switch (section.type) {
    case SectionType.COLUMN_MAPPING:
      if (section.id === SectionId.INSTRUMENT_IDENTIFIERS) {
        return (
          <InstrumentIdentifiers
            sectionId={section.id}
            templateId={templateId}
            instrumentType={instrumentType}
            expanded={expanded}
            onChange={onChange}
            availableHeaders={availableHeaders}
          />
        );
      }
      return null;

    case SectionType.SINGLE_COLUMN:
      return (
        <SingleColumnSection
          title={section.title}
          sectionId={section.id}
          templateId={templateId}
          instrumentType={instrumentType}
          expanded={expanded}
          onChange={onChange}
          availableHeaders={availableHeaders}
        />
      );

    case SectionType.SINGLE_COLUMN_WITH_DATE:
      return (
        <SingleColumnWithDateSection
          title={section.title}
          sectionId={section.id}
          templateId={templateId}
          instrumentType={instrumentType}
          expanded={expanded}
          onChange={onChange}
          availableHeaders={availableHeaders}
        />
      );

    case SectionType.SINGLE_COLUMN_WITH_LABELS:
      return (
        <SingleColumnWithLabelSection
          title={section.title}
          sectionId={section.id}
          templateId={templateId}
          instrumentType={instrumentType}
          expanded={expanded}
          onChange={onChange}
          availableHeaders={availableHeaders}
        />
      );

    case SectionType.MATH_OPERATIONS:
      return (
        <MathOperationsSection
          title={section.title}
          sectionId={section.id}
          templateId={templateId}
          instrumentType={instrumentType}
          expanded={expanded}
          onChange={onChange}
          availableHeaders={availableHeaders}
        />
      );

    case SectionType.CURRENCY:
      return (
        <CurrencySection
          title={section.title}
          sectionId={section.id}
          templateId={templateId}
          instrumentType={instrumentType}
          expanded={expanded}
          onChange={onChange}
          availableHeaders={availableHeaders}
        />
      );

    case SectionType.MULTI_COLUMN_RULES_WITH_CATEGORIES:
      // Sections that need sign detection use MultiColumnRulesBinaryCategoriesSection
      if (section.id === SectionId.BUY_SELL) {
        return (
          <MultiColumnRulesBinaryCategoriesSection
            title={section.title}
            sectionId={section.id}
            templateId={templateId}
            instrumentType={instrumentType}
            expanded={expanded}
            onChange={onChange}
            availableHeaders={availableHeaders}
            categories={BUY_SELL_CATEGORIES}
          />
        );
      }
      if (section.id === SectionId.DEPOSIT_WITHDRAW_TYPE) {
        const displayTitle =
          instrumentType === 'Deposit/Withdraw'
            ? 'Deposit/Withdraw'
            : section.title;

        return (
          <MultiColumnRulesBinaryCategoriesSection
            title={displayTitle}
            sectionId={section.id}
            templateId={templateId}
            instrumentType={instrumentType}
            expanded={expanded}
            onChange={onChange}
            availableHeaders={availableHeaders}
            categories={DEPOSIT_WITHDRAW_CATEGORIES}
          />
        );
      }
      if (section.id === SectionId.FX_SPOT_TYPE) {
        const displayTitle =
          instrumentType === 'FX Spot' ? 'Buy/Sell' : section.title;

        return (
          <MultiColumnRulesBinaryCategoriesSection
            title={displayTitle}
            sectionId={section.id}
            templateId={templateId}
            instrumentType={instrumentType}
            expanded={expanded}
            onChange={onChange}
            availableHeaders={availableHeaders}
            categories={FX_SPOT_CATEGORIES}
          />
        );
      }
      if (section.id === SectionId.DEDUCTION_REBATE) {
        return (
          <MultiColumnRulesBinaryCategoriesSection
            title={section.title}
            sectionId={section.id}
            templateId={templateId}
            instrumentType={instrumentType}
            expanded={expanded}
            onChange={onChange}
            availableHeaders={availableHeaders}
            categories={DEDUCTION_REBATE_CATEGORIES}
          />
        );
      }
      // Sections without sign detection use MultiColumnRulesWithCategoriesSection
      if (section.id === SectionId.FEE_TYPE) {
        const displayTitle =
          instrumentType === 'Cash Adjustments' ? 'Fee Type' : section.title;

        return (
          <MultiColumnRulesWithCategoriesSection
            title={displayTitle}
            sectionId={section.id}
            templateId={templateId}
            instrumentType={instrumentType}
            expanded={expanded}
            onChange={onChange}
            availableHeaders={availableHeaders}
            categories={FEE_TYPE_CATEGORIES}
          />
        );
      }
      if (section.id === SectionId.SETTLEMENT_ACCOUNT) {
        return (
          <MultiColumnRulesWithCategoriesSection
            title={section.title}
            sectionId={section.id}
            templateId={templateId}
            instrumentType={instrumentType}
            expanded={expanded}
            onChange={onChange}
            availableHeaders={availableHeaders}
            categories={SETTLEMENT_ACCOUNT_CATEGORIES}
          />
        );
      }
      return null;

    default:
      return null;
  }
};
